import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload, FileText, ArrowLeft, ArrowRight, Sparkles, FlaskConical,
  Activity, BarChart3, Droplet,
  X, Clock, Check, ChevronRight, Database, ScrollText, Layers,
  Microscope, Beaker, Lock, Wand2, AlertTriangle
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ComposedChart, ReferenceLine
} from "recharts";
import SEO from "@/components/SEO";
import ReproducibleAIBadge from "@/components/ReproducibleAIBadge";
import FtirReportSections from "@/components/FtirReportSections";
import UspCorrectiveActions from "@/components/UspCorrectiveActions";
import { InstrumentParamsFields, InstrumentParamsBox, hasInstrumentFields } from "@/components/InstrumentParams";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/analytics";
import { logger } from '@/lib/logger';
import "./DemoPage.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sample files mapped to analysis types
const SAMPLES = [
  { type: "ftir-spectrum",    label: "FTIR Spectrum",       file: "/samples/sample_ftir.csv",   Icon: Microscope },
  { type: "hplc-chromatogram",label: "HPLC Chromatogram",   file: "/samples/sample_hplc.csv",   Icon: Droplet },
];

const ANALYSIS_TYPES = [
  { value: "ftir-spectrum",     name: "FTIR / IR",   desc: "Functional groups" },
  { value: "hplc-chromatogram", name: "HPLC",        desc: "Peaks, retention" },
];

const isChromatography = (t) =>
  t === "gc-chromatogram" || t === "hplc-chromatogram" || t?.includes("chromatogram");

const getUnits = (t) => {
  if (isChromatography(t)) return { position: "min", intensity: "mAU" };
  if (t === "ftir-spectrum") return { position: "cm⁻¹", intensity: "%T" };
  if (t === "uv-vis-spectrum" || t === "nir-spectrum") return { position: "nm", intensity: "Abs" };
  return { position: "", intensity: "" };
};

const getAnalysisLabel = (t) => {
  const m = {
    "ftir-spectrum": "FTIR Spectrum",
    "uv-vis-spectrum": "UV-Vis Spectrum",
    "nir-spectrum": "NIR Spectrum",
    "hplc-chromatogram": "HPLC Chromatogram",
    "gc-chromatogram": "GC Chromatogram",
  };
  return m[t] || t;
};

const confidenceTier = (c = "") => {
  // Accept either the legacy string ("High (92%)", "Medium", "Low") or the
  // new numeric `match_confidence_percent` (0-100). Numeric wins when given.
  const n = typeof c === "number" ? c : parseFloat(c);
  if (!Number.isNaN(n) && Number.isFinite(n)) {
    if (n >= 80) return "high";
    if (n >= 50) return "medium";
    return "low";
  }
  const v = String(c).toLowerCase();
  if (v.includes("high")) return "high";
  if (v.includes("med"))  return "medium";
  return "low";
};

// Format whatever confidence field the AI returned into a human label.
// Prefers the numeric `match_confidence_percent` (new schema) and falls back
// to the legacy `confidence` string.
const formatConfidence = (obj) => {
  if (!obj) return "—";
  const n = obj.match_confidence_percent;
  if (typeof n === "number" && Number.isFinite(n)) return `${Math.round(n)}%`;
  if (obj.confidence) return obj.confidence;
  return "—";
};

const DemoPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedFile, setSelectedFile] = useState(null);
  const [analysisType, setAnalysisType] = useState("ftir-spectrum");
  const [isUploading, setIsUploading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [demoResult, setDemoResult] = useState(null);
  const [demoUsed, setDemoUsed] = useState(false);
  const [activeTab, setActiveTab] = useState("example");
  const [progressStep, setProgressStep] = useState(0); // 0..4
  const [instrumentParams, setInstrumentParams] = useState({});

  // Page view event (variant: "redesign-v1" — change when you ship another variant)
  useEffect(() => {
    track("demo_page_viewed", { variant: "redesign-v1" });
  }, []);

  const switchTab = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    track("demo_tab_switched", { tab, variant: "redesign-v1" });
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
      toast.success(`File "${acceptedFiles[0].name}" selected`);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "text/plain": [".txt"],
      "application/pdf": [".pdf"],
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
    },
    multiple: false,
  });

  const handleSampleClick = (sample) => {
    setAnalysisType(sample.type);
    track("demo_sample_downloaded", {
      sample_type: sample.type,
      sample_label: sample.label,
      variant: "redesign-v1",
    });
    // Trigger download
    const a = document.createElement("a");
    a.href = sample.file;
    a.download = sample.file.split("/").pop();
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success(`Sample "${sample.label}" downloaded — drop it in the upload zone above`);
  };

  const removeFile = (e) => {
    e?.stopPropagation();
    setSelectedFile(null);
  };

  const handleDemoAnalyze = async () => {
    if (!selectedFile) { toast.error("Please select a file first"); return; }
    if (!analysisType) { toast.error("Please select an analysis type"); return; }
    if (demoUsed)     { toast.error("Demo limit reached. Please register for more analyses."); return; }

    try {
      setIsUploading(true);
      setProgressStep(1);

      track("demo_analyze_clicked", {
        analysis_type: analysisType,
        file_name: selectedFile.name,
        file_size_kb: Math.round(selectedFile.size / 1024),
        variant: "redesign-v1",
      });

      const formData = new FormData();
      formData.append("file", selectedFile);

      const uploadResponse = await axios.post(`${API}/demo/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const fileId = uploadResponse.data.file_id;
      setIsUploading(false);
      setIsAnalyzing(true);
      setProgressStep(2);

      // Animate to "AI interpretation" step shortly after
      setTimeout(() => setProgressStep(3), 800);

      const analysisResponse = await axios.post(`${API}/demo/analyze`, {
        file_id: fileId,
        analysis_type: analysisType,
        language: user?.language || 'en',
        instrument_params: hasInstrumentFields(analysisType) ? instrumentParams : null,
      });

      setProgressStep(4);
      setDemoResult(analysisResponse.data);
      setDemoUsed(true);
      track("demo_analyze_succeeded", {
        analysis_type: analysisType,
        peak_count: analysisResponse.data?.chart_data?.peaks?.length ?? 0,
        has_material: !!analysisResponse.data?.material_inference?.predicted_material_class,
        variant: "redesign-v1",
      });
      toast.success("Demo analysis completed!");
    } catch (error) {
      logger.error(error);
      track("demo_analyze_failed", {
        analysis_type: analysisType,
        error: error.response?.data?.detail || error.message || "unknown",
        variant: "redesign-v1",
      });
      toast.error(error.response?.data?.detail || "Analysis failed. Please try again.");
      setProgressStep(0);
    } finally {
      setIsUploading(false);
      setIsAnalyzing(false);
    }
  };

  const isBusy = isUploading || isAnalyzing;
  const progressPct = (progressStep / 4) * 100;

  return (
    <div className="demo-root" data-testid="demo-page">
      <SEO
        title="Try Demo - Free Spectral Analysis"
        description="Try TabedLab's AI-powered spectral analysis for free. Upload sample FTIR or HPLC data and see instant results with peak detection and compound identification."
        keywords="free spectral analysis demo, FTIR demo, FTIR analyzer, HPLC analyzer, chromatography tool, AI lab analysis demo"
        url="/demo"
      />

      {/* NAV */}
      <nav className="demo-nav">
        <button className="demo-nav-logo" onClick={() => navigate("/")} data-testid="logo-home-link">
          <img src="/tabedlab-logo.png" alt="TabedLab" />
          <span>TabedLab</span>
        </button>
        <div className="demo-nav-right">
          <button
            className="demo-btn-ghost"
            onClick={() => navigate("/")}
            data-testid="back-home-link"
          >
            <ArrowLeft size={14} /> Back
          </button>
          <button
            className="demo-btn-primary"
            onClick={() => {
              track("demo_signup_cta_clicked", { placement: "top_nav", variant: "redesign-v1" });
              navigate("/register");
            }}
            data-testid="register-button"
          >
            Sign Up Free
          </button>
        </div>
      </nav>

      <div className="demo-page">
        {/* HEADER */}
        <header className="demo-page-header">
          <p className="demo-eyebrow">Spectral &amp; chromatographic analysis</p>
          <h1 className="demo-page-title">
            Drop your data,<br />
            get a <em>complete interpretation</em>
          </h1>
          <p className="demo-page-sub">
            Results in under 30 seconds — no account required for your first analysis.
          </p>
        </header>

        {!demoResult ? (
          <div className="demo-layout">
            {/* ─── LEFT: UPLOAD + CONFIG ─── */}
            <div>
              <div className="demo-panel">
                {/* UPLOAD */}
                <div className="demo-panel-section">
                  <div className="demo-panel-label">Data file</div>
                  <div
                    {...getRootProps()}
                    className={`demo-upload-zone${isDragActive ? " dragging" : ""}`}
                    data-testid="demo-file-dropzone"
                  >
                    <input {...getInputProps()} />
                    <Upload size={28} strokeWidth={1.5} style={{ display: "block", margin: "0 auto 10px" }} />
                    {selectedFile ? (
                      <>
                        <div className="demo-upload-title">{selectedFile.name}</div>
                        <div className="demo-upload-sub">
                          {(selectedFile.size / 1024).toFixed(2)} KB · click to replace
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="demo-upload-title">
                          {isDragActive ? "Drop file here" : "Drag your file here"}
                        </div>
                        <div className="demo-upload-sub">or click to browse</div>
                        <div className="demo-upload-formats">
                          {["CSV", "Excel", "TXT", "PDF", "PNG", "JPG"].map((f) => (
                            <span key={f} className="demo-fmt-tag">{f}</span>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  {selectedFile && (
                    <div className="demo-file-indicator" data-testid="demo-file-indicator">
                      <FileText size={14} />
                      <span className="demo-file-name">{selectedFile.name}</span>
                      <button
                        className="demo-file-remove"
                        onClick={removeFile}
                        aria-label="Remove file"
                        data-testid="demo-file-remove"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>

                {/* SAMPLES */}
                <div className="demo-panel-section">
                  <div className="demo-panel-label">No data? Try a sample file</div>
                  <div className="demo-samples-grid">
                    {SAMPLES.map((s) => {
                      const Icon = s.Icon;
                      return (
                        <button
                          key={s.type}
                          className={`demo-sample-btn${analysisType === s.type ? " active" : ""}`}
                          onClick={() => handleSampleClick(s)}
                          data-testid={`sample-${s.type}`}
                        >
                          <Icon size={15} strokeWidth={1.6} />
                          <span>{s.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="demo-sample-hint">Download a sample, then drop it in the upload zone above.</p>
                </div>

                {/* ANALYSIS TYPE */}
                <div className="demo-panel-section">
                  <div className="demo-panel-label">Analysis type</div>
                  <div className="demo-type-grid" data-testid="demo-type-grid">
                    {ANALYSIS_TYPES.map((t) => (
                      <button
                        key={t.value}
                        className={`demo-type-btn${analysisType === t.value ? " selected" : ""}`}
                        onClick={() => setAnalysisType(t.value)}
                        data-testid={`type-${t.value}`}
                      >
                        <span className="demo-type-name">{t.name}</span>
                        <span className="demo-type-desc">{t.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {hasInstrumentFields(analysisType) && (
                  <div className="demo-panel-section">
                    <InstrumentParamsFields analysisType={analysisType} value={instrumentParams} onChange={setInstrumentParams} dark />
                  </div>
                )}

                {/* CTA */}
                <div className="demo-cta-wrap">
                  {isBusy && (
                    <>
                      <div className="demo-progress-bar">
                        <div className="demo-progress-fill" style={{ width: `${progressPct}%` }} />
                      </div>
                      <div className="demo-progress-steps">
                        {[
                          "Reading file…",
                          "Detecting peaks…",
                          "AI interpretation…",
                          "Generating report…",
                        ].map((label, idx) => {
                          const step = idx + 1;
                          const status = progressStep > step ? "done" : progressStep === step ? "active" : "";
                          return (
                            <div key={label} className={`demo-progress-step ${status}`}>
                              <div className="demo-step-dot" />
                              {label}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}

                  <button
                    className="demo-btn-analyze"
                    onClick={handleDemoAnalyze}
                    disabled={!selectedFile || !analysisType || isBusy || demoUsed}
                    data-testid="demo-analyze-button"
                  >
                    {isUploading ? (
                      <>Uploading…</>
                    ) : isAnalyzing ? (
                      <><Activity size={16} className="animate-pulse" /> AI Analyzing…</>
                    ) : demoUsed ? (
                      <><Lock size={16} /> Demo used — sign up for more</>
                    ) : (
                      <><Sparkles size={16} /> Analyze with AI</>
                    )}
                  </button>
                  <p className="demo-btn-timing">
                    <Clock size={11} /> Results in under 30 seconds
                  </p>
                </div>
              </div>
            </div>

            {/* ─── RIGHT: PREVIEW ─── */}
            <div>
              <div className="demo-preview-panel">
                <div className="demo-preview-tabs">
                  <button
                    className={`demo-preview-tab${activeTab === "example" ? " active" : ""}`}
                    onClick={() => switchTab("example")}
                    data-testid="tab-example"
                  >
                    Example result
                  </button>
                  <button
                    className={`demo-preview-tab${activeTab === "caps" ? " active" : ""}`}
                    onClick={() => switchTab("caps")}
                    data-testid="tab-capabilities"
                  >
                    Capabilities
                  </button>
                </div>

                <div className="demo-preview-content">
                  {activeTab === "example" ? (
                    <>
                      <div className="demo-spectrum-container">
                        <div className="demo-spectrum-title">FTIR Spectrum — Example (polystyrene)</div>
                        <svg width="100%" viewBox="0 0 340 92" role="img" aria-label="Example FTIR spectrum">
                          <defs>
                            <linearGradient id="demoSg" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#2dd4bf" stopOpacity="0.25" />
                              <stop offset="100%" stopColor="#2dd4bf" stopOpacity="0" />
                            </linearGradient>
                          </defs>
                          <line x1="30" y1="10" x2="330" y2="10" stroke="rgba(99,178,255,0.08)" strokeWidth="0.5" />
                          <line x1="30" y1="30" x2="330" y2="30" stroke="rgba(99,178,255,0.08)" strokeWidth="0.5" />
                          <line x1="30" y1="50" x2="330" y2="50" stroke="rgba(99,178,255,0.08)" strokeWidth="0.5" />
                          <line x1="30" y1="70" x2="330" y2="70" stroke="rgba(99,178,255,0.08)" strokeWidth="0.5" />
                          <line x1="30" y1="75" x2="330" y2="75" stroke="rgba(99,178,255,0.15)" strokeWidth="0.5" />
                          <path d="M30,25 C50,24 60,23 70,22 C80,21 85,18 90,12 C95,6 98,4 100,6 C102,8 104,18 106,22 C110,24 120,23 130,22 C140,21 145,14 148,8 C151,3 153,2 155,4 C157,7 159,18 162,22 C170,24 185,23 195,22 C205,21 210,19 213,15 C216,11 218,7 220,5 C222,3 224,5 226,9 C228,13 230,19 235,22 C248,24 265,23 275,22 C280,21 283,17 286,12 C289,7 291,5 293,6 C295,8 297,16 300,21 C305,23 315,24 330,24" fill="none" stroke="#2dd4bf" strokeWidth="1.5" strokeLinecap="round" />
                          <path d="M30,25 C50,24 60,23 70,22 C80,21 85,18 90,12 C95,6 98,4 100,6 C102,8 104,18 106,22 C110,24 120,23 130,22 C140,21 145,14 148,8 C151,3 153,2 155,4 C157,7 159,18 162,22 C170,24 185,23 195,22 C205,21 210,19 213,15 C216,11 218,7 220,5 C222,3 224,5 226,9 C228,13 230,19 235,22 C248,24 265,23 275,22 C280,21 283,17 286,12 C289,7 291,5 293,6 C295,8 297,16 300,21 C305,23 315,24 330,24 L330,75 L30,75 Z" fill="url(#demoSg)" />
                          <line x1="100" y1="6"  x2="100" y2="75" stroke="rgba(251,191,36,0.4)" strokeWidth="0.8" strokeDasharray="3,2" />
                          <line x1="155" y1="2"  x2="155" y2="75" stroke="rgba(251,191,36,0.6)" strokeWidth="0.8" strokeDasharray="3,2" />
                          <line x1="220" y1="5"  x2="220" y2="75" stroke="rgba(251,191,36,0.5)" strokeWidth="0.8" strokeDasharray="3,2" />
                          <text x="100" y="86" textAnchor="middle" fontFamily="'DM Mono',monospace" fontSize="7" fill="rgba(251,191,36,0.85)">3026</text>
                          <text x="155" y="86" textAnchor="middle" fontFamily="'DM Mono',monospace" fontSize="7" fill="rgba(251,191,36,0.85)">1601</text>
                          <text x="220" y="86" textAnchor="middle" fontFamily="'DM Mono',monospace" fontSize="7" fill="rgba(251,191,36,0.85)">756</text>
                          <text x="30"  y="83" fontFamily="'DM Mono',monospace" fontSize="7" fill="rgba(100,116,139,0.8)">4000</text>
                          <text x="300" y="83" fontFamily="'DM Mono',monospace" fontSize="7" fill="rgba(100,116,139,0.8)">500</text>
                          <text x="175" y="91" textAnchor="middle" fontFamily="'DM Mono',monospace" fontSize="6" fill="rgba(100,116,139,0.6)">Wavenumber (cm⁻¹)</text>
                        </svg>
                      </div>

                      <table className="demo-peaks-table">
                        <thead>
                          <tr>
                            <th>Peak (cm⁻¹)</th>
                            <th>Intensity</th>
                            <th>Assignment</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td><span className="demo-peak-val">3026</span></td>
                            <td><span className="demo-peak-badge demo-peak-med">Medium</span></td>
                            <td>Aromatic C–H (stretch)</td>
                          </tr>
                          <tr>
                            <td><span className="demo-peak-val">1601</span></td>
                            <td><span className="demo-peak-badge demo-peak-strong">Strong</span></td>
                            <td>Aromatic C=C (stretch)</td>
                          </tr>
                          <tr>
                            <td><span className="demo-peak-val">756</span></td>
                            <td><span className="demo-peak-badge demo-peak-strong">Strong</span></td>
                            <td>C–H oop, monosubstituted</td>
                          </tr>
                          <tr>
                            <td><span className="demo-peak-val">698</span></td>
                            <td><span className="demo-peak-badge demo-peak-med">Medium</span></td>
                            <td>Phenyl ring deformation</td>
                          </tr>
                        </tbody>
                      </table>

                      <div className="demo-ai-block">
                        <div className="demo-ai-label">
                          <Sparkles size={11} /> AI Interpretation
                        </div>
                        <p className="demo-ai-text">
                          The spectrum shows a strong absorption at <strong>1601 cm⁻¹</strong> characteristic
                          of aromatic C=C stretching, combined with a band at <strong>756 cm⁻¹</strong>
                          typical of a monosubstituted phenyl ring. The overall profile is consistent with a
                          <strong> styrenic polymer</strong>.
                        </p>
                      </div>

                      <div className="demo-compound-match">
                        <div>
                          <div className="demo-compound-name">Polystyrene (PS)</div>
                          <div className="demo-compound-sub">NIST · PubChem CID 24978 · MW 104.15</div>
                        </div>
                        <div>
                          <div className="demo-confidence-val">94%</div>
                          <div className="demo-confidence-lbl">Confidence</div>
                        </div>
                      </div>

                      <div className="demo-signup-nudge">
                        <p className="demo-nudge-text">
                          <Sparkles size={13} style={{ color: "var(--teal)", flexShrink: 0, marginTop: 2 }} />
                          <span>
                            <strong>Your report is ready.</strong> Create a free account to download the
                            full PDF and run more analyses.
                          </span>
                        </p>
                        <button
                          className="demo-btn-signup"
                          onClick={() => {
                            track("demo_signup_cta_clicked", { placement: "preview_nudge", variant: "redesign-v1" });
                            navigate("/register");
                          }}
                          data-testid="preview-signup-cta"
                        >
                          Create a free account <ArrowRight size={14} />
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="demo-cap-list">
                      <div className="demo-cap-item">
                        <div className="demo-cap-icon teal">
                          <BarChart3 size={16} strokeWidth={1.6} />
                        </div>
                        <div>
                          <div className="demo-cap-name">Peak detection &amp; interpretation</div>
                          <div className="demo-cap-desc">
                            Automatic identification with detailed chemical annotation for each spectral band.
                          </div>
                        </div>
                      </div>

                      <div className="demo-cap-item">
                        <div className="demo-cap-icon blue">
                          <FlaskConical size={16} strokeWidth={1.6} />
                        </div>
                        <div>
                          <div className="demo-cap-name">Functional group analysis</div>
                          <div className="demo-cap-desc">
                            Identification of carbonyl, hydroxyl, amine and other groups with confidence levels.
                          </div>
                        </div>
                      </div>

                      <div className="demo-cap-item">
                        <div className="demo-cap-icon purple">
                          <Database size={16} strokeWidth={1.6} />
                        </div>
                        <div>
                          <div className="demo-cap-name">Reference database comparison</div>
                          <div className="demo-cap-desc">Matching with reference spectra from:</div>
                          <div className="demo-db-badges">
                            <span className="demo-db-badge">NIST WebBook</span>
                            <span className="demo-db-badge">SDBS (AIST)</span>
                            <span className="demo-db-badge">PubChem</span>
                          </div>
                        </div>
                      </div>

                      <div className="demo-cap-item">
                        <div className="demo-cap-icon amber">
                          <ScrollText size={16} strokeWidth={1.6} />
                        </div>
                        <div>
                          <div className="demo-cap-name">Scientific PDF report</div>
                          <div className="demo-cap-desc">
                            Publication-ready report — annotated figures, peak table, interpretive synthesis.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* ═══ RESULT VIEW ═══ */
          <ResultView
            demoResult={demoResult}
            analysisType={analysisType}
            navigate={navigate}
          />
        )}
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════ */
/* RESULT VIEW                                                       */
/* ════════════════════════════════════════════════════════════════ */
const ResultView = ({ demoResult, analysisType, navigate }) => {
  const units = getUnits(demoResult.analysis_type || analysisType);
  const isChromo = isChromatography(demoResult.analysis_type || analysisType);

  return (
    <div className="demo-result-panel" data-testid="demo-result-panel">
      <div className="demo-result-header">
        <h2 className="demo-result-title">
          <Sparkles size={22} style={{ color: "var(--teal)" }} />
          <span>Research-Grade <em>Analysis Results</em></span>
        </h2>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <ReproducibleAIBadge variant="compact" />
          <span className="demo-result-badge">{getAnalysisLabel(analysisType)}</span>
        </div>
      </div>

      {/* Baseline-correction warning — rendered FIRST and loud, because
          downstream quantitative values are unreliable until %T is
          brought below 100. */}
      {demoResult.baseline_warning && (
        <div
          className="demo-result-block"
          data-testid="baseline-warning-banner"
          style={{
            background: "rgba(239, 68, 68, 0.08)",
            border: "1px solid rgba(239, 68, 68, 0.45)",
            borderLeft: "4px solid #dc2626",
            borderRadius: 8,
            padding: "12px 16px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <AlertTriangle size={16} color="#dc2626" />
            <span style={{ color: "#dc2626", fontSize: 13, fontWeight: 700, letterSpacing: 0.3 }}>
              Baseline correction required
            </span>
          </div>
          <div style={{ color: "var(--text2)", fontSize: 12.5, lineHeight: 1.5 }}>
            {demoResult.baseline_warning}
          </div>
        </div>
      )}

      <InstrumentParamsBox rows={demoResult.instrument_parameters} />

      {/* Executive Summary */}
      {demoResult.executive_summary && (
        <div className="demo-result-block">
          <div className="demo-section-h">
            <FileText size={11} /> Executive Summary
          </div>
          <div className="demo-summary-box">
            <p>{demoResult.executive_summary}</p>
          </div>
        </div>
      )}

      {/* Material identification */}
      {demoResult.material_inference?.predicted_material_class && (
        <div className="demo-result-block">
          <div className="demo-section-h">
            <Beaker size={11} /> Material Identification
          </div>
          <div className="demo-material-card">
            <div className="demo-material-head">
              <span className="demo-material-class">
                {demoResult.material_inference.predicted_material_class}
              </span>
              <span className={`demo-confidence-pill ${confidenceTier(demoResult.material_inference.confidence)}`}>
                {demoResult.material_inference.confidence}
              </span>
            </div>
            {demoResult.material_inference.supporting_evidence?.length > 0 && (
              <div className="demo-evidence-list">
                {demoResult.material_inference.supporting_evidence.slice(0, 3).map((e, i) => (
                  <div key={`evidence-${i}-${(e || "").slice(0, 24)}`}>• {e}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* FTIR report moved to below the spectrum chart for canonical order */}

      {/* Spectrum chart */}
      {demoResult.chart_data?.x && (
        <div className="demo-result-block">
          <div className="demo-section-h blue">
            <Activity size={11} /> {getAnalysisLabel(analysisType)}
          </div>

          {demoResult.chart_data.processing_applied?.length > 0 && (
            <div className="demo-proc-chips">
              {demoResult.chart_data.processing_applied.map((p) => (
                <span key={`proc-${p}`} className="demo-proc-chip">{p}</span>
              ))}
            </div>
          )}

          <div className="demo-chart-card">
            <div className="demo-chart-white">
              <ResponsiveContainer width="100%" height={380}>
                <ComposedChart
                  data={demoResult.chart_data.x.map((x, i) => ({ x, y: demoResult.chart_data.y[i] }))}
                  margin={{ top: 20, right: 30, left: 50, bottom: 50 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="x"
                    stroke="#374151"
                    tick={{ fill: "#374151", fontSize: 11 }}
                    label={{
                      value: demoResult.chart_data.xlabel || `Wavenumber (${units.position})`,
                      position: "insideBottom",
                      offset: -10,
                      fill: "#1f2937",
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                    reversed={analysisType === "ftir-spectrum"}
                    domain={["dataMin", "dataMax"]}
                  />
                  <YAxis
                    stroke="#374151"
                    tick={{ fill: "#374151", fontSize: 11 }}
                    label={{
                      value: demoResult.chart_data.ylabel || `Intensity (${units.intensity})`,
                      angle: -90,
                      position: "insideLeft",
                      fill: "#1f2937",
                      fontSize: 12,
                      fontWeight: 500,
                      dx: -10,
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #d1d5db",
                      borderRadius: 6,
                      color: "#1f2937",
                    }}
                    formatter={(value) => [
                      typeof value === "number"
                        ? `${value.toFixed(2)} ${units.intensity}`
                        : value,
                      isChromo ? "Signal" : "Intensity",
                    ]}
                    labelFormatter={(label) =>
                      `${typeof label === "number" ? label.toFixed(isChromo ? 2 : 0) : label} ${units.position}`
                    }
                  />
                  {demoResult.chart_data.peaks?.map((peak, i) => (
                    <ReferenceLine
                      key={`pk-${i}`}
                      x={peak.x}
                      stroke="#dc2626"
                      strokeDasharray="4 4"
                      strokeWidth={1}
                      label={{
                        value: peak.x?.toFixed(isChromo ? 2 : 0),
                        position: "top",
                        fill: "#dc2626",
                        fontSize: 9,
                        offset: 5,
                      }}
                    />
                  ))}
                  <Line
                    type="monotone"
                    dataKey="y"
                    stroke="#0d9488"
                    strokeWidth={1.5}
                    dot={false}
                    activeDot={{ r: 4, fill: "#0d9488", stroke: "#fff", strokeWidth: 2 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {demoResult.chart_data.peaks?.length > 0 && (
              <div className="demo-peak-pills">
                <div className="demo-peak-pills-label">
                  Detected peaks ({demoResult.chart_data.peaks.length})
                </div>
                {demoResult.chart_data.peaks.map((peak, i) => (
                  <span key={`pk-${peak.x ?? i}-${peak.intensity ?? ""}`} className="demo-peak-pill">
                    {isChromo
                      ? `${peak.x?.toFixed(2)} ${units.position} (${peak.intensity?.toFixed(1)} ${units.intensity})`
                      : `${peak.x?.toFixed(0)} ${units.position} (${peak.intensity?.toFixed(1)}${units.intensity})`}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── FTIR REPORT (Sections 1-5) — rendered after the spectrum chart ─── */}
      {demoResult.ftir_report && (
        <FtirReportSections report={demoResult.ftir_report} />
      )}

      {/* ─── HPLC / GC quantitative report ─── */}
      {demoResult.hplc_metrics?.length > 0 && (
        <div className="demo-result-block">
          <div className="demo-section-h blue">
            <BarChart3 size={11} /> Quantitative Peak Report (USP / Ph. Eur.)
          </div>

          {/* USP <621> System Suitability banner */}
          {demoResult.system_suitability && (
            <div
              data-testid="system-suitability-banner"
              data-status={demoResult.system_suitability.status?.toLowerCase()}
              style={{
                marginBottom: 14,
                padding: "12px 14px",
                borderRadius: 6,
                border: `1px solid ${
                  demoResult.system_suitability.all_pass
                    ? "rgba(34,197,94,0.45)"
                    : demoResult.system_suitability.status === "Fail"
                    ? "rgba(239,68,68,0.45)"
                    : "rgba(234,179,8,0.45)"
                }`,
                background: demoResult.system_suitability.all_pass
                  ? "rgba(34,197,94,0.06)"
                  : demoResult.system_suitability.status === "Fail"
                  ? "rgba(239,68,68,0.06)"
                  : "rgba(234,179,8,0.06)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <span
                  style={{
                    fontFamily: "var(--mono)",
                    fontSize: 10,
                    letterSpacing: 1,
                    fontWeight: 700,
                    padding: "3px 8px",
                    borderRadius: 4,
                    color: "#fff",
                    background: demoResult.system_suitability.all_pass
                      ? "#16a34a"
                      : demoResult.system_suitability.status === "Fail"
                      ? "#dc2626"
                      : "#ca8a04",
                  }}
                  data-testid="system-suitability-status"
                >
                  {demoResult.system_suitability.all_pass ? "PASS" : demoResult.system_suitability.status?.toUpperCase()}
                </span>
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text)" }}>
                  USP &lt;621&gt; System Suitability
                </span>
              </div>
              <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 10 }}>
                {demoResult.system_suitability.summary}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 }}>
                {(demoResult.system_suitability.criteria || []).map((c, idx) => (
                  <div
                    key={`crit-${idx}`}
                    data-testid={`suitability-criterion-${idx}`}
                    style={{
                      padding: "6px 8px",
                      borderRadius: 4,
                      background: "var(--surface2, rgba(255,255,255,0.04))",
                      borderLeft: `3px solid ${
                        c.pass === true ? "#16a34a" : c.pass === false ? "#dc2626" : "#9ca3af"
                      }`,
                    }}
                  >
                    <div style={{ fontSize: 10, color: "var(--text3)", fontFamily: "var(--mono)", textTransform: "uppercase", letterSpacing: 0.5 }}>
                      {c.name}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text2)", fontFamily: "var(--mono)", marginTop: 2 }}>
                      {c.observed} <span style={{ color: "var(--text3)" }}>(req. {c.threshold})</span>
                    </div>
                  </div>
                ))}
              </div>
              <UspCorrectiveActions suitability={demoResult.system_suitability} />
            </div>
          )}

          <div style={{ overflowX: "auto" }}>
            <table className="demo-table" data-testid="hplc-metrics-table">
              <thead>
                <tr>
                  <th>Peak</th>
                  <th>Rt (min)</th>
                  <th>Area (mAU·min)</th>
                  <th>Height (mAU)</th>
                  <th>% Area</th>
                  <th>W½ (min)</th>
                  <th>As</th>
                  <th>N (plates)</th>
                  <th>Rs vs prev.</th>
                  <th>Compound</th>
                </tr>
              </thead>
              <tbody>
                {demoResult.hplc_metrics.map((p, i) => {
                  const isUnknown = (p.compound_label || "").startsWith("Unknown");
                  const N = p.theoretical_plates;
                  const As = p.symmetry_factor;
                  const Rs = p.resolution_to_previous;
                  return (
                    <tr key={`hplc-${i}`} data-testid={`hplc-peak-row-${i}`}>
                      <td><span className="demo-mono">{i + 1}</span></td>
                      <td><span className="demo-mono">{p.retention_time?.toFixed(3)}</span></td>
                      <td><span className="demo-mono">{p.area?.toFixed(2)}</span></td>
                      <td><span className="demo-mono">{p.height?.toFixed(2) ?? "—"}</span></td>
                      <td><span className="demo-mono">{p.relative_area?.toFixed(2)} %</span></td>
                      <td><span className="demo-mono">{p.width_half_height?.toFixed(4) ?? "—"}</span></td>
                      <td>
                        <span className="demo-mono">{As?.toFixed(3) ?? "—"}</span>
                        {As && (As < 0.8 || As > 1.5) && (
                          <span style={{ display: "block", fontSize: 10, color: "var(--warn)" }}>
                            outside 0.8–1.5
                          </span>
                        )}
                      </td>
                      <td>
                        <span className="demo-mono">{N ?? "—"}</span>
                        {N && N < 2000 && (
                          <span style={{ display: "block", fontSize: 10, color: "var(--warn)" }}>
                            low efficiency
                          </span>
                        )}
                      </td>
                      <td>
                        <span className="demo-mono">{Rs?.toFixed(2) ?? "—"}</span>
                        {Rs !== null && Rs !== undefined && Rs < 1.5 && (
                          <span style={{ display: "block", fontSize: 10, color: "var(--warn)" }}>
                            not baseline (Rs &lt; 1.5)
                          </span>
                        )}
                      </td>
                      <td style={{ fontSize: 12, color: isUnknown ? "var(--text3)" : "var(--text)", fontStyle: isUnknown ? "italic" : "normal" }}>
                        {p.compound_label}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 11, color: "var(--text3)", marginTop: 8, fontFamily: "var(--mono)", lineHeight: 1.6 }}>
            <strong style={{ color: "var(--text2)" }}>Formulas:</strong>
            &nbsp;Area: trapezoidal integration of (signal − baseline).
            &nbsp;W½: full width at half height.
            &nbsp;As: USP symmetry factor at 5 % height = (a + b) / 2a.
            &nbsp;N = 5.54 × (Rt / W½)².
            &nbsp;Rs = 1.18 × (Rt₂ − Rt₁) / (W½₁ + W½₂).
          </p>
        </div>
      )}

      {/* ─── Chromatographic conditions ─── */}
      {demoResult.chromatographic_conditions && (
        <div className="demo-result-block">
          <div className="demo-section-h">
            <FlaskConical size={11} /> Chromatographic Conditions
          </div>
          <div className="demo-chart-card" data-testid="chromatographic-conditions">
            {demoResult.chromatographic_conditions.is_default && (
              <div style={{
                padding: "10px 12px",
                marginBottom: 12,
                background: "rgba(251, 191, 36, 0.08)",
                border: "1px solid rgba(251, 191, 36, 0.22)",
                borderRadius: 8,
                fontSize: 12,
                color: "var(--warn)"
              }}>
                ⚠ {demoResult.chromatographic_conditions.note || "Defaults shown — please edit before publishing."}
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
              {Object.entries(demoResult.chromatographic_conditions)
                .filter(([k]) => !["is_default", "note"].includes(k))
                .map(([key, value]) => {
                  const label = key
                    .replace(/_/g, " ")
                    .replace(/\bml min\b/i, "(mL/min)")
                    .replace(/\bnm\b/i, "(nm)")
                    .replace(/\bc\b/i, "(°C)")
                    .replace(/\bul\b/i, "(µL)");
                  return (
                    <div key={key} style={{
                      background: "var(--navy3)",
                      border: "1px solid var(--border)",
                      borderRadius: 8,
                      padding: "10px 12px"
                    }}>
                      <div style={{ fontSize: 10, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, fontFamily: "var(--mono)" }}>
                        {label}
                      </div>
                      <div style={{ fontSize: 13, color: value ? "var(--text)" : "var(--text3)" }}>
                        {value || "— not provided —"}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}

      {/* Detailed Peaks (suppressed for FTIR — replaced by the FTIR enhanced peak table) */}
      {demoResult.peak_analysis_detailed?.length > 0 && !demoResult.ftir_report && (
        <div className="demo-result-block">
          <div className="demo-section-h blue">
            <BarChart3 size={11} /> Detailed Peak Analysis
          </div>
          <div style={{ overflowX: "auto" }}>
            <table className="demo-table">
              <thead>
                <tr>
                  <th>{isChromo ? "Retention time" : "Position"}</th>
                  <th>{isChromo ? "Area / Height" : "Intensity"}</th>
                  <th>{isChromo ? "Compound" : "Assignment"}</th>
                  <th>Scientific interpretation</th>
                  <th>Confidence</th>
                </tr>
              </thead>
              <tbody>
                {demoResult.peak_analysis_detailed.slice(0, 8).map((peak, i) => {
                  const position = peak.position_cm || peak.position || peak.retention_time;
                  return (
                    <tr key={`pad-${position ?? i}-${peak.assignment ?? peak.compound ?? ""}`}>
                      <td><span className="demo-mono">{position} {units.position}</span></td>
                      <td>{peak.intensity || peak.area}</td>
                      <td>
                        <span style={{ color: "var(--text)", fontWeight: 500 }}>
                          {peak.assignment || peak.compound}
                        </span>
                        {peak.bond_type && (
                          <span style={{ display: "block", color: "var(--text3)", fontSize: 11, marginTop: 2 }}>
                            {peak.bond_type}
                          </span>
                        )}
                      </td>
                      <td style={{ fontSize: 11.5, color: "var(--text3)", maxWidth: 320 }}>
                        {peak.interpretation || (peak.literature_range && `Literature: ${peak.literature_range}`)}
                      </td>
                      <td>
                        <span className={`demo-confidence-pill ${confidenceTier(peak.match_confidence_percent ?? peak.confidence)}`}>
                          {formatConfidence(peak)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Functional Groups */}
      {demoResult.functional_groups_identified?.length > 0 && (
        <div className="demo-result-block">
          <div className="demo-section-h purple">
            <FlaskConical size={11} /> Functional Groups Identified
          </div>
          <div className="demo-groups-grid">
            {demoResult.functional_groups_identified.slice(0, 6).map((g, i) => (
              <div key={`fg-${g.group ?? i}`} className="demo-group-card">
                <div className="demo-group-card-top">
                  <span className="demo-group-name">{g.group}</span>
                  <span className={`demo-confidence-pill ${confidenceTier(g.confidence)}`}>
                    {g.confidence}
                  </span>
                </div>
                {g.evidence_peaks && (
                  <div className="demo-group-meta">Evidence: {g.evidence_peaks.join(", ")}</div>
                )}
                {g.structural_role && (
                  <div className="demo-group-meta">{g.structural_role}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Legacy functional_groups list */}
      {(!demoResult.functional_groups_identified?.length) && demoResult.functional_groups?.length > 0 && (
        <div className="demo-result-block">
          <div className="demo-section-h purple">
            <FlaskConical size={11} /> Functional Groups Identified
          </div>
          <div>
            {demoResult.functional_groups.map((g, i) => (
              <span
                key={`fg-legacy-${typeof g === "string" ? g : g.group ?? i}`}
                className="demo-peak-pill"
                style={{ background: "rgba(167,139,250,0.1)", color: "#c4b5fd", borderColor: "rgba(167,139,250,0.25)" }}
              >
                {typeof g === "string" ? g : g.group}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Compound identification */}
      {demoResult.compound_identification?.length > 0 && (
        <div className="demo-result-block">
          <div className="demo-section-h amber">
            <Database size={11} /> Compound Identification
          </div>
          <div className="demo-compound-list">
            {demoResult.compound_identification.slice(0, 5).map((c, i) => {
              const isString = typeof c === "string";
              const name = isString ? c : (c.compound_name || c.name || "Unknown");
              const conf = isString ? null : (c.confidence || c.confidence_score);
              const matching = isString ? null : c.matching_peaks;
              const rt = isString ? null : (c.retention_time || c.rt);
              return (
                <div key={`cmpd-${name}-${rt ?? i}`} className="demo-compound-row">
                  <span className="demo-compound-rank">{i + 1}.</span>
                  <div className="demo-compound-body">
                    <span className="demo-compound-name-l">{name}</span>
                    {conf && (
                      <span className={`demo-confidence-pill ${confidenceTier(conf)}`}>{conf}</span>
                    )}
                    {isChromo && rt && (
                      <div className="demo-compound-meta">Retention time: {rt} min</div>
                    )}
                    {!isChromo && matching?.length > 0 && (
                      <div className="demo-compound-meta">
                        Matching peaks: {Array.isArray(matching) ? matching.join(", ") : matching}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Publication paragraph */}
      {demoResult.publication_paragraph && (
        <div className="demo-result-block">
          <div className="demo-section-h green">
            <FileText size={11} /> Publication-Ready Paragraph
          </div>
          <div className="demo-publication-box">
            <p>&ldquo;{demoResult.publication_paragraph}&rdquo;</p>
            <div className="demo-publication-foot">
              Formatted for the Results / Discussion section of scientific papers.
            </div>
          </div>
        </div>
      )}

      {/* Research Guidance & Insights — last actionable block */}
      {demoResult.user_guidance?.length > 0 && (
        <div className="demo-result-block">
          <div className="demo-section-h amber">
            <Wand2 size={11} /> Research Guidance &amp; Insights
          </div>
          <div className="demo-guidance-list">
            {demoResult.user_guidance.slice(0, 4).map((g, i) => (
              <div key={`guidance-${i}-${(g || "").slice(0, 24)}`} className="demo-guidance-item">
                <Sparkles size={14} />
                <span>{g}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Final CTA */}
      <div className="demo-cta-final">
        <div className="demo-cta-final-title">
          Unlock <em>full research-grade analysis</em>
        </div>
        <p className="demo-cta-final-sub">
          PDF report downloads, comparative analysis, unlimited analyses, and more advanced features.
        </p>
        <div className="demo-cta-final-actions">
          <button
            className="demo-btn-final primary"
            onClick={() => {
              track("demo_signup_cta_clicked", { placement: "result_final", variant: "redesign-v1" });
              navigate("/register");
            }}
            data-testid="demo-signup-cta"
          >
            <Sparkles size={14} /> Create free account
          </button>
          <button
            className="demo-btn-final ghost"
            onClick={() => {
              track("demo_compare_cta_clicked", { placement: "result_final", variant: "redesign-v1" });
              navigate("/compare");
            }}
            data-testid="compare-spectra-cta"
          >
            <Layers size={14} /> Compare multiple spectra
          </button>
        </div>
      </div>
    </div>
  );
};

export default DemoPage;
